import { useState } from "react";
import usePageLoader from '../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerChart, ShimmerTable } from '../components/common/Shimmer';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  FiMessageCircle, FiThumbsUp, FiThumbsDown, FiUsers,
  FiZap, FiTrendingUp, FiTrendingDown, FiCheckCircle,
  FiStar, FiDownload,
} from "react-icons/fi";
import { BiConversation } from "react-icons/bi";

/* ─── Data ─── */
const DATA_7D = [
  { day: "May 5",  conversations: 14, messages: 112 },
  { day: "May 6",  conversations: 18, messages: 143 },
  { day: "May 7",  conversations: 28, messages: 220 },
  { day: "May 8",  conversations: 22, messages: 176 },
  { day: "May 9",  conversations: 31, messages: 248 },
  { day: "May 10", conversations: 27, messages: 216 },
  { day: "May 11", conversations: 35, messages: 280 },
];

const DATA_30D = [
  { day: "Apr 12", conversations: 9,  messages: 72  },
  { day: "Apr 15", conversations: 14, messages: 110 },
  { day: "Apr 18", conversations: 11, messages: 88  },
  { day: "Apr 21", conversations: 20, messages: 159 },
  { day: "Apr 24", conversations: 17, messages: 134 },
  { day: "Apr 27", conversations: 24, messages: 192 },
  { day: "Apr 30", conversations: 19, messages: 152 },
  { day: "May 3",  conversations: 26, messages: 208 },
  { day: "May 6",  conversations: 18, messages: 144 },
  { day: "May 9",  conversations: 31, messages: 248 },
  { day: "May 11", conversations: 35, messages: 280 },
];

const TOP_QUESTIONS = [
  { question: "What is your pricing?",          count: 84 },
  { question: "Do you offer a free trial?",     count: 67 },
  { question: "How do I reset my password?",    count: 55 },
  { question: "How to integrate with HubSpot?", count: 43 },
  { question: "What languages are supported?",  count: 38 },
  { question: "Can I export chat history?",     count: 29 },
  { question: "Is there a mobile app?",         count: 24 },
  { question: "How to train on a PDF?",         count: 21 },
];

const PEAK_HOURS = [
  { hour: "6 AM",  count: 8  },
  { hour: "7 AM",  count: 14 },
  { hour: "8 AM",  count: 27 },
  { hour: "9 AM",  count: 48 },
  { hour: "10 AM", count: 74 },
  { hour: "11 AM", count: 82 },
  { hour: "12 PM", count: 65 },
  { hour: "1 PM",  count: 71 },
  { hour: "2 PM",  count: 78 },
  { hour: "3 PM",  count: 69 },
  { hour: "4 PM",  count: 55 },
  { hour: "5 PM",  count: 43 },
  { hour: "6 PM",  count: 31 },
  { hour: "7 PM",  count: 22 },
  { hour: "8 PM",  count: 15 },
];

const TRAFFIC_SOURCES = [
  { source: "Google Search",  visitors: 512, pct: 40, color: "#4285F4" },
  { source: "Direct",         visitors: 321, pct: 25, color: "#4F46E5" },
  { source: "LinkedIn",       visitors: 193, pct: 15, color: "#0A66C2" },
  { source: "Twitter / X",    visitors: 129, pct: 10, color: "#1DA1F2" },
  { source: "Product Hunt",   visitors:  77, pct:  6, color: "#DA552F" },
  { source: "Other",          visitors:  52, pct:  4, color: "#9CA3AF" },
];

const COUNTRIES = [
  { flag: "🇮🇳", country: "India",         visitors: 542, pct: 42 },
  { flag: "🇺🇸", country: "United States", visitors: 257, pct: 20 },
  { flag: "🇬🇧", country: "United Kingdom",visitors: 129, pct: 10 },
  { flag: "🇸🇬", country: "Singapore",     visitors:  90, pct:  7 },
  { flag: "🇩🇪", country: "Germany",       visitors:  77, pct:  6 },
  { flag: "🇦🇺", country: "Australia",     visitors:  64, pct:  5 },
];

const DEVICES = [
  { device: "Desktop", icon: "🖥", pct: 58 },
  { device: "Mobile",  icon: "📱", pct: 34 },
  { device: "Tablet",  icon: "📟", pct:  8 },
];

const BROWSERS = [
  { browser: "Chrome",  pct: 62 },
  { browser: "Safari",  pct: 20 },
  { browser: "Firefox", pct: 10 },
  { browser: "Edge",    pct:  5 },
  { browser: "Other",   pct:  3 },
];

const FALLBACK_QUESTIONS = [
  { question: "Can you process a refund for me?",       asked: 23, fallbackRate: "18%" },
  { question: "Connect me to a human agent",            asked: 19, fallbackRate: "15%" },
  { question: "What is your SLA for enterprise?",       asked: 14, fallbackRate: "11%" },
  { question: "I need my invoice from last year",       asked: 11, fallbackRate: "9%"  },
  { question: "Can you book a demo call for me?",       asked:  9, fallbackRate: "7%"  },
];

const RECENT_CONVERSATIONS = [
  { id: "C-1084", user: "arun@gmail.com",     messages: 8,  duration: "3:42", started: "2h ago",  resolved: true,  feedback: "positive" },
  { id: "C-1083", user: "priya@techcorp.com", messages: 4,  duration: "1:15", started: "3h ago",  resolved: true,  feedback: "negative" },
  { id: "C-1082", user: "Anonymous",          messages: 12, duration: "5:30", started: "5h ago",  resolved: false, feedback: "none"     },
  { id: "C-1081", user: "swetha@startup.io",  messages: 6,  duration: "2:10", started: "6h ago",  resolved: true,  feedback: "positive" },
  { id: "C-1080", user: "rahul@company.in",   messages: 9,  duration: "4:05", started: "1d ago",  resolved: true,  feedback: "positive" },
  { id: "C-1079", user: "meena@design.co",    messages: 3,  duration: "0:58", started: "1d ago",  resolved: false, feedback: "none"     },
  { id: "C-1078", user: "suresh@b2b.com",     messages: 15, duration: "7:22", started: "2d ago",  resolved: true,  feedback: "positive" },
  { id: "C-1077", user: "kavitha@saas.app",   messages: 5,  duration: "1:48", started: "2d ago",  resolved: true,  feedback: "negative" },
];

function AnalyticsSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <ShimmerStatCard key={i} />)}
        </div>
        <ShimmerChart h="h-64" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ShimmerChart h="h-48" />
          <ShimmerChart h="h-48" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white border rounded-xl p-4 space-y-3">
              <ShimmerBox h="h-4" w="w-32" />
              {[...Array(5)].map((_, j) => (
                <div key={j} className="flex items-center gap-3">
                  <ShimmerBox h="h-3" w="w-20" />
                  <ShimmerBox h="h-2" w="w-full" rounded="rounded-full" />
                  <ShimmerBox h="h-3" w="w-8" />
                </div>
              ))}
            </div>
          ))}
        </div>
        <ShimmerTable rows={5} cols={6} />
        <ShimmerTable rows={8} cols={5} />
      </div>
    </div>
  );
}

/* ─── Page ─── */
export default function Analytics() {
  const [range, setRange] = useState("7d");
  const loading = usePageLoader();

  const trendData = range === "7d" ? DATA_7D : DATA_30D;

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* ── Sticky Header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track chatbot performance and user engagement</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date range selector */}
          <div className="flex items-center border rounded-lg overflow-hidden text-sm">
            {["7d", "30d", "90d"].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-3.5 py-1.5 font-medium transition ${
                  range === r ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {r === "7d" ? "7 Days" : r === "30d" ? "30 Days" : "90 Days"}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-2 px-3.5 py-1.5 border rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">
            <FiDownload size={14} /> Export
          </button>
        </div>
      </div>

      {loading ? <AnalyticsSkeleton /> : <div className="flex-1 overflow-y-auto p-6 space-y-6">

        {/* ── KPI Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <KpiCard icon={FiUsers}          label="Total Visitors"     value="1,284" delta="+12%" up />
          <KpiCard icon={BiConversation}   label="Conversations"      value="438"   delta="+8%"  up />
          <KpiCard icon={FiMessageCircle}  label="Messages Sent"      value="2,940" delta="+21%" up />
          <KpiCard icon={FiZap}            label="Avg. Response"      value="1.2s"  delta="-0.3s" up iconColor="text-yellow-500" />
          <KpiCard icon={FiCheckCircle}    label="Resolution Rate"    value="87%"   delta="+4%"  up iconColor="text-green-500" />
          <KpiCard icon={FiStar}           label="CSAT Score"         value="4.3/5" delta="+0.2" up iconColor="text-amber-500" />
        </div>

        {/* ── Trend + Traffic Sources ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* Conversations & Messages trend */}
          <div className="xl:col-span-2 bg-white border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">Conversations & Messages</h2>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-primary inline-block rounded" /> Conversations</span>
                <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-cyan-500 inline-block rounded" /> Messages</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="gradConv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#4F46E5" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradMsg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#06B6D4" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="conversations" stroke="#4F46E5" strokeWidth={2} fill="url(#gradConv)" dot={{ r: 3 }} />
                <Area type="monotone" dataKey="messages"      stroke="#06B6D4" strokeWidth={2} fill="url(#gradMsg)"  dot={{ r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Traffic Sources */}
          <div className="bg-white border rounded-xl p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Traffic Sources</h2>
            <div className="space-y-3">
              {TRAFFIC_SOURCES.map((src) => (
                <div key={src.source}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-700 font-medium">{src.source}</span>
                    <span className="text-gray-500">{src.visitors.toLocaleString()} ({src.pct}%)</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${src.pct}%`, backgroundColor: src.color }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Top Questions + Peak Hours ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* Top Questions */}
          <div className="bg-white border rounded-xl p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Top Questions Asked</h2>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={TOP_QUESTIONS} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="question" type="category" width={200} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#4F46E5" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Peak Hours */}
          <div className="bg-white border rounded-xl p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Peak Activity Hours</h2>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={PEAK_HOURS}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#4F46E5" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Countries + Devices & Browsers ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

          {/* Top Countries */}
          <div className="bg-white border rounded-xl p-5">
            <h2 className="font-semibold text-gray-900 mb-4">Top Countries</h2>
            <div className="space-y-3">
              {COUNTRIES.map((c) => (
                <div key={c.country} className="flex items-center gap-3">
                  <span className="text-lg w-6 shrink-0">{c.flag}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-700 font-medium">{c.country}</span>
                      <span className="text-gray-500">{c.visitors.toLocaleString()} ({c.pct}%)</span>
                    </div>
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-primary rounded-full" style={{ width: `${c.pct}%` }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Devices + Browsers */}
          <div className="flex flex-col gap-6">

            <div className="bg-white border rounded-xl p-5">
              <h2 className="font-semibold text-gray-900 mb-4">Device Breakdown</h2>
              <div className="space-y-3">
                {DEVICES.map((d) => (
                  <div key={d.device} className="flex items-center gap-3">
                    <span className="text-base w-6">{d.icon}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-700 font-medium">{d.device}</span>
                        <span className="text-gray-500">{d.pct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${d.pct}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white border rounded-xl p-5">
              <h2 className="font-semibold text-gray-900 mb-4">Browser Breakdown</h2>
              <div className="space-y-3">
                {BROWSERS.map((b) => (
                  <div key={b.browser} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-700 font-medium">{b.browser}</span>
                        <span className="text-gray-500">{b.pct}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${b.pct}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* ── Feedback Overview ── */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          <SummaryTile label="Positive Feedback"  value="74%" sub="323 ratings"   color="text-green-600" bg="bg-green-50"  icon={FiThumbsUp} />
          <SummaryTile label="Negative Feedback"  value="26%" sub="114 ratings"   color="text-red-500"   bg="bg-red-50"    icon={FiThumbsDown} />
          <SummaryTile label="Avg. Session Length" value="3m 22s" sub="per conversation" color="text-blue-600" bg="bg-blue-50" icon={FiZap} />
          <SummaryTile label="Bot Resolution Rate" value="87%" sub="no escalation needed" color="text-indigo-600" bg="bg-indigo-50" icon={FiCheckCircle} />
        </div>

        {/* ── Fallback / Unanswered Questions ── */}
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">Unanswered Questions</h2>
              <p className="text-xs text-gray-500 mt-0.5">Questions your bot couldn't answer — train on these to improve</p>
            </div>
            <span className="text-xs bg-red-100 text-red-600 px-2.5 py-1 rounded-full font-medium">
              {FALLBACK_QUESTIONS.length} gaps found
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="px-5 py-3 text-left">Question</th>
                <th className="px-5 py-3 text-center">Times Asked</th>
                <th className="px-5 py-3 text-center">Fallback Rate</th>
                <th className="px-5 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {FALLBACK_QUESTIONS.map((fq, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-5 py-3 text-gray-800">{fq.question}</td>
                  <td className="px-5 py-3 text-center text-gray-600 font-medium">{fq.asked}</td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">{fq.fallbackRate}</span>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <button className="text-xs text-primary hover:underline font-medium">Add to training</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Recent Conversations ── */}
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Recent Conversations</h2>
            <span className="text-xs text-gray-500">{RECENT_CONVERSATIONS.length} conversations</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wide">
                <tr>
                  <th className="px-5 py-3 text-left">ID</th>
                  <th className="px-5 py-3 text-left">User</th>
                  <th className="px-5 py-3 text-center">Messages</th>
                  <th className="px-5 py-3 text-center">Duration</th>
                  <th className="px-5 py-3 text-left">Started</th>
                  <th className="px-5 py-3 text-center">Resolved</th>
                  <th className="px-5 py-3 text-center">Feedback</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {RECENT_CONVERSATIONS.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono text-xs text-gray-500">{c.id}</td>
                    <td className="px-5 py-3 text-gray-800 font-medium">{c.user}</td>
                    <td className="px-5 py-3 text-center text-gray-600">{c.messages}</td>
                    <td className="px-5 py-3 text-center text-gray-600 font-mono text-xs">{c.duration}</td>
                    <td className="px-5 py-3 text-gray-500">{c.started}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        c.resolved ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                      }`}>
                        {c.resolved ? "Resolved" : "Open"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        c.feedback === "positive" ? "bg-green-100 text-green-700" :
                        c.feedback === "negative" ? "bg-red-100 text-red-600" :
                        "bg-gray-100 text-gray-400"
                      }`}>
                        {c.feedback === "none" ? "—" : c.feedback}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>}
    </div>
  );
}

/* ─── Components ─── */
function KpiCard({ icon: Icon, label, value, delta, up, iconColor = "text-primary" }) {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <Icon className={iconColor} size={15} />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${up ? "text-green-600" : "text-red-500"}`}>
        {up ? <FiTrendingUp size={11} /> : <FiTrendingDown size={11} />}
        {delta} <span className="text-gray-400 font-normal">vs last period</span>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, sub, color, bg, icon: Icon }) {
  return (
    <div className={`${bg} rounded-xl p-4 flex items-center gap-3`}>
      <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm`}>
        <Icon className={color} size={18} />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-xl font-bold ${color}`}>{value}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
      </div>
    </div>
  );
}
